import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import './Details.css'
import { ComponentReport, ConditionState } from '../../types'
import criticalTag from '../../assets/critical_tag.png'
import minorTag from '../../assets/minor_tag.png'
import normalTag from '../../assets/normal_tag.png'

interface DetailsProps {
  componentReports: ComponentReport[]
}

function ChevronIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" className="details__chevron-svg">
      <path d="M7.5 4L13.5 10L7.5 16" stroke="#999999" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

export default function Details({ componentReports }: DetailsProps) {
  const { t } = useTranslation()
  const critical = componentReports.filter(r => r.conditionState === 'CRITICAL')
  const minor = componentReports.filter(r => r.conditionState === 'MINOR')
  const normal = componentReports.filter(r => r.conditionState === 'NORMAL')

  const [expanded, setExpanded] = useState<Record<ConditionState, boolean>>({
    CRITICAL: true,
    MINOR: true,
    NORMAL: false,
  })

  const toggle = (state: ConditionState) => {
    setExpanded(prev => ({ ...prev, [state]: !prev[state] }))
  }

  const getTagImage = (state: ConditionState) => {
    switch (state) {
      case 'CRITICAL': return criticalTag
      case 'MINOR': return minorTag
      case 'NORMAL': return normalTag
    }
  }

  const getStatusText = (state: ConditionState) => {
    switch (state) {
      case 'CRITICAL': return t('details.critical')
      case 'MINOR': return t('details.minor')
      case 'NORMAL': return t('details.good')
    }
  }

  const renderGroup = (reports: ComponentReport[], state: ConditionState) => {
    if (reports.length === 0) return null
    const isExpanded = expanded[state]

    return (
      <div key={state} className="details__group">
        <button
          className="details__group-header"
          onClick={() => toggle(state)}
          aria-expanded={isExpanded}
          aria-label={`${getStatusText(state)}, ${isExpanded ? 'collapse' : 'expand'}`}
        >
          <img
            src={getTagImage(state)}
            alt={getStatusText(state)}
            className="details__tag"
          />
          <span className={`details__chevron${isExpanded ? ' details__chevron--expanded' : ''}`}>
            <ChevronIcon />
          </span>
        </button>

        <div className={`details__collapse${isExpanded ? ' details__collapse--expanded' : ''}`}>
          <div className="details__collapse-inner">
            <div className="details__divider details__divider--after-tag" />
            {reports.map((report, index) => (
              <div key={report.id} className="details__item-wrapper">
                {index > 0 && <div className="details__divider" />}
                <div className={`details__item ${index === 0 ? 'details__item--first' : ''}`}>
                  <span className="details__component-name">{report.name}</span>
                  <span className="details__component-condition">
                    {report.condition || (state === 'NORMAL' ? t('details.issue_not_found') : '')}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    )
  }

  const hasAnyGroups = critical.length > 0 || minor.length > 0 || normal.length > 0

  return (
    <div className="details">
      <h3 className="details__label">{t('details.title')}</h3>
      {renderGroup(critical, 'CRITICAL')}
      {critical.length > 0 && (minor.length > 0 || normal.length > 0) && (
        <div className="details__divider details__divider--between-groups" />
      )}
      {renderGroup(minor, 'MINOR')}
      {minor.length > 0 && normal.length > 0 && (
        <div className="details__divider details__divider--between-groups" />
      )}
      {renderGroup(normal, 'NORMAL')}
      {hasAnyGroups && (
        <div className="details__divider details__divider--after-last" />
      )}
    </div>
  )
}
